import { FormEvent, useEffect, useRef, useState } from "react";
import {
  getCompanyProfile,
  updateCompanyProfile,
  uploadCompanyLogo,
  deleteCompanyLogo,
  CompanyProfile,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";

type FormState = Omit<CompanyProfile, "id">;

const EMPTY: FormState = {
  name: "",
  legalForm: "",
  ico: "",
  dic: "",
  isVatPayer: false,
  street: "",
  city: "",
  zip: "",
  country: "CZ",
  email: "",
  phone: "",
  website: "",
  bankAccount: "",
  iban: "",
  bankName: "",
  logoUrl: "",
  invoiceNote: "",
};

export default function CompanyTab() {
  const toast = useToast();
  const [state, setState] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef<HTMLInputElement>(null);

  async function onLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const profile = await uploadCompanyLogo(file);
      setState({ ...EMPTY, ...profile, ...nullsToEmpty(profile) });
      toast.show("success", "Logo nahráno.");
    } catch (err: any) {
      toast.show("error", err.message ?? "Upload selhal.");
    } finally {
      setLogoUploading(false);
      if (logoFileRef.current) logoFileRef.current.value = "";
    }
  }

  async function onLogoDelete() {
    if (!confirm("Opravdu smazat logo?")) return;
    try {
      const profile = await deleteCompanyLogo();
      setState({ ...EMPTY, ...profile, ...nullsToEmpty(profile) });
      toast.show("success", "Logo smazáno.");
    } catch (err: any) {
      toast.show("error", err.message ?? "Smazání selhalo.");
    }
  }

  useEffect(() => {
    getCompanyProfile()
      .then((p) => setState({ ...EMPTY, ...p, ...nullsToEmpty(p) }))
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setState((s) => ({ ...s, [k]: v }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await updateCompanyProfile(state);
      setState({ ...EMPTY, ...saved, ...nullsToEmpty(saved) });
      toast.show("success", "Firemní profil uložen.");
    } catch (err: any) {
      toast.show("error", err.message ?? "Uložení selhalo.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-slate-500">Načítám…</div>;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Section title="Název a právní forma">
        <Grid2>
          <Field label="Název firmy / jméno" required>
            <input className="input" value={state.name} onChange={(e) => update("name", e.target.value)} required />
          </Field>
          <Field label="Právní forma" hint="např. s.r.o., OSVČ…">
            <input className="input" value={state.legalForm ?? ""} onChange={(e) => update("legalForm", e.target.value)} />
          </Field>
          <Field label="IČO">
            <input className="input" value={state.ico ?? ""} onChange={(e) => update("ico", e.target.value)} />
          </Field>
          <Field label="DIČ">
            <input className="input" value={state.dic ?? ""} onChange={(e) => update("dic", e.target.value)} />
          </Field>
        </Grid2>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={state.isVatPayer}
            onChange={(e) => update("isVatPayer", e.target.checked)}
            className="h-4 w-4"
          />
          Plátce DPH
        </label>
      </Section>

      <Section title="Adresa sídla (zároveň výchozí depot — odkud jezdíte)">
        <Grid2>
          <Field label="Ulice a číslo" required>
            <input className="input" value={state.street} onChange={(e) => update("street", e.target.value)} required />
          </Field>
          <Field label="Město" required>
            <input className="input" value={state.city} onChange={(e) => update("city", e.target.value)} required />
          </Field>
          <Field label="PSČ" required>
            <input className="input" value={state.zip} onChange={(e) => update("zip", e.target.value)} required />
          </Field>
          <Field label="Země">
            <input className="input" value={state.country} onChange={(e) => update("country", e.target.value)} />
          </Field>
        </Grid2>
      </Section>

      <Section title="Kontaktní údaje (zobrazí se na webu)">
        <Grid2>
          <Field label="E-mail" required>
            <input type="email" className="input" value={state.email} onChange={(e) => update("email", e.target.value)} required />
          </Field>
          <Field label="Telefon">
            <input className="input" value={state.phone ?? ""} onChange={(e) => update("phone", e.target.value)} />
          </Field>
          <Field label="Web">
            <input className="input" value={state.website ?? ""} onChange={(e) => update("website", e.target.value)} placeholder="https://kouzelnaobloha.cz" />
          </Field>
          <Field label="Logo" hint="PNG, JPG, WebP nebo SVG (max 3 MB). Propíše se na web, do faktur a e-mailů.">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-36 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {state.logoUrl ? (
                  <img src={state.logoUrl} alt="Logo" className="max-h-full max-w-full object-contain p-2" />
                ) : (
                  <span className="text-xs text-slate-400">bez loga</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={onLogoUpload}
                />
                <button
                  type="button"
                  onClick={() => logoFileRef.current?.click()}
                  disabled={logoUploading}
                  className="btn-secondary text-sm"
                >
                  {logoUploading ? "Nahrávám…" : state.logoUrl ? "Změnit logo" : "Nahrát logo"}
                </button>
                {state.logoUrl && (
                  <button
                    type="button"
                    onClick={onLogoDelete}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Odebrat
                  </button>
                )}
              </div>
            </div>
          </Field>
        </Grid2>
      </Section>

      <Section title="Bankovní účet (pro faktury a QR platbu)">
        <Grid2>
          <Field label="Číslo účtu" hint='formát "123456789/0100"'>
            <input className="input" value={state.bankAccount ?? ""} onChange={(e) => update("bankAccount", e.target.value)} />
          </Field>
          <Field label="IBAN" hint="pro QR SPD platbu">
            <input className="input" value={state.iban ?? ""} onChange={(e) => update("iban", e.target.value)} />
          </Field>
          <Field label="Název banky">
            <input className="input" value={state.bankName ?? ""} onChange={(e) => update("bankName", e.target.value)} />
          </Field>
        </Grid2>
      </Section>

      <Section title="Poznámka na fakturách">
        <textarea
          className="input min-h-20"
          value={state.invoiceNote ?? ""}
          onChange={(e) => update("invoiceNote", e.target.value)}
          placeholder="Např. Děkujeme za spolupráci."
        />
      </Section>

      <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? "Ukládám…" : "Uložit firemní profil"}
        </button>
      </div>
    </form>
  );
}

// ─── malé UI helpery ───────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-base font-semibold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 md:grid-cols-2">{children}</div>;
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

// API vrací null pro nevyplněné — převedeme na "", aby kontrolované inputy nebrečely
function nullsToEmpty(p: Partial<CompanyProfile>): Partial<FormState> {
  const out: any = {};
  for (const [k, v] of Object.entries(p)) {
    out[k] = v === null ? "" : v;
  }
  return out;
}
